import { useState, useEffect, useRef, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '../Modal'
import { Btn } from '../Btn'

export interface CodeInputModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (code: string) => void | Promise<void>
  error?: string | null
  submitting?: boolean
}

/** Crockford Base32 alphabet — no I, L, O, U */
const CROCKFORD = new Set('0123456789ABCDEFGHJKMNPQRSTVWXYZ')

function normalizeInput(raw: string): string {
  return raw
    .toUpperCase()
    .split('')
    .filter((c) => CROCKFORD.has(c))
    .slice(0, 6)
    .join('')
}

export function CodeInputModal({
  open,
  onClose,
  onSubmit,
  error,
  submitting = false,
}: CodeInputModalProps): ReactElement {
  const { t } = useTranslation()
  const [code, setCode] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const prevOpen = useRef(open)

  // Reset input when modal re-opens (false → true transition)
  useEffect(() => {
    if (!prevOpen.current && open) {
      setCode('')
    }
    prevOpen.current = open
  }, [open])

  // Focus input when modal opens
  useEffect(() => {
    if (open) {
      // Small defer so the modal renders before focus
      const id = setTimeout(() => inputRef.current?.focus(), 50)
      return () => clearTimeout(id)
    }
  }, [open])

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setCode(normalizeInput(e.target.value))
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter' && code.length === 6 && !submitting) {
      void onSubmit(code)
    }
  }

  function handleSubmit(): void {
    if (code.length === 6 && !submitting) {
      void onSubmit(code)
    }
  }

  const submitDisabled = code.length !== 6 || submitting

  return (
    <Modal open={open} onClose={onClose} title={t('liveMatch.room.joinTitle')}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <input
          ref={inputRef}
          type="text"
          inputMode="text"
          value={code}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={t('liveMatch.room.codePlaceholder')}
          maxLength={6}
          aria-label={t('liveMatch.room.codePlaceholder')}
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 24,
            fontWeight: 700,
            letterSpacing: '0.15em',
            textAlign: 'center',
            padding: '12px 16px',
            borderRadius: 'var(--radius-md, 12px)',
            border: error
              ? '2px solid var(--danger)'
              : '2px solid var(--tg-link-color, #3390ec)',
            background: 'var(--bg-elev-1)',
            color: 'var(--tg-text-color)',
            width: '100%',
            boxSizing: 'border-box',
            outline: 'none',
          }}
        />

        {error != null && error !== '' && (
          <p
            role="alert"
            style={{
              color: 'var(--danger)',
              fontSize: 13,
              margin: 0,
              textAlign: 'center',
            }}
          >
            {error}
          </p>
        )}

        <div style={{ display: 'flex', gap: 'var(--space-2, 8px)' }}>
          <Btn variant="ghost" block onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Btn>
          <Btn
            variant="primary"
            block
            disabled={submitDisabled}
            onClick={handleSubmit}
          >
            {submitting ? '…' : t('liveMatch.room.joinAction')}
          </Btn>
        </div>
      </div>
    </Modal>
  )
}
